// 9fbef606107a605d69c0edbcd8029e5d
import { createSelector } from 'reselect';
import { router } from 'next';
import isEmpty from 'lodash/isEmpty';
import toLower from 'lodash/toLower';
import { isTier2Device, isTier3Device } from '@tcp/web/src/utils/device-tiering';
import {
  getIsGuest,
  isPlccUser,
  isRememberedUser,
} from '../../../account/User/container/User.selectors';
import {
  generateGroups,
  removeEmptyFilters,
} from '../../ProductListing/container/ProductListing.util';
import { getAPIConfig, flattenArray, parseBoolean } from '../../../../../utils';
import { PRODUCTS_PER_LOAD, PRODUCTS_PER_LOAD_LOWER_END_DEVICES } from './SearchDetail.constants';
import {
  SESSIONCONFIG_REDUCER_KEY,
  SLP_PAGE_REDUCER_KEY,
} from '../../../../../constants/reducer.constants';
import { getIsClickableBrandBadgesEnabled } from '../../../../../reduxStore/selectors/session.selectors';
import { getFeatureBrand, isFeatureBrandPage } from './SearchDetail.util';

const getReducer = (state) => state[SLP_PAGE_REDUCER_KEY];

const getSearchListingState = (state) => {
  return state[SLP_PAGE_REDUCER_KEY];
};

const noResponse = {};

export const getSlpUrl = createSelector(getSearchListingState, (slpData) => slpData.pageUrl);

const getSlpProducts = (state) => getReducer(state).products;

export default getSlpProducts;

export const giftCardProducts = (state) => getReducer(state).giftCardProducts;

const getOrganizedHeaderNavigationTree = (state) => {
  const unorganizedTree = state.Navigation.navigationData;

  // Only in browser memory, will be cleaned on page re-fresh
  // if (cachedOrganizedNavTree) {
  //   return cachedOrganizedNavTree;
  // }

  const organizedNav =
    unorganizedTree &&
    unorganizedTree.map((L1) => {
      return {
        ...L1,
        menuGroupings: generateGroups(L1),
      };
    });

  // only on browser so we dont need to keep deriving this
  if (/* isClient() && */ organizedNav && organizedNav.length) {
    // TODO - fix this - cachedOrganizedNavTree = organizedNav;
  }

  return organizedNav;
};

export const getCurrentListingIds = createSelector(
  getSearchListingState,
  (products) => products && products.currentNavigationIds
);

export const getIsSearchPage = createSelector(
  getSearchListingState,
  (ProductListing) => ProductListing && ProductListing.isSearch
);

export const getNavigationTree = (state) => {
  // const currentListingIds = state.productListing.breadcrumbs.map(crumb => crumb.pathSuffix);
  const currentListingIds = getCurrentListingIds(state);
  const navTree = getOrganizedHeaderNavigationTree(state);
  return (
    currentListingIds &&
    currentListingIds[0] &&
    navTree &&
    navTree.find((L1) => L1.categoryId === currentListingIds[0])
  );
};

export const getBreadCrumbTrail = createSelector(
  getSearchListingState,
  (products) => products && products.breadCrumbTrail
);

export const getProductsSelect = createSelector(
  getSearchListingState,
  (products) => products && products.loadedProductsPages && products.loadedProductsPages[0]
);

export const getAllProductsSelect = createSelector(getSearchListingState, (products) => {
  const allProducts = products && products.loadedProductsPages;
  return allProducts && flattenArray(allProducts);
});

export const getTotalProductsCount = createSelector(
  getSearchListingState,
  (products) => products && products.totalProductsCount
);

export const getCurrentSearchForText = createSelector(
  getSearchListingState,
  getIsClickableBrandBadgesEnabled,
  (products, isFeatureBrandEnabled) => {
    if (isFeatureBrandEnabled && isFeatureBrandPage(router?.state, isFeatureBrandEnabled)) {
      return getFeatureBrand(router?.state, isFeatureBrandEnabled);
    }
    return products && products.currentListingSearchForText;
  }
);
export const getSearchTerm = createSelector(
  getSearchListingState,
  (products) => products && products.searchTerm
);

export const getAppliedFilters = createSelector(
  getSearchListingState,
  (products) => products && products.appliedFiltersIds
);

export const getAppliedFilterIds = createSelector(
  (state) => getSearchListingState(state)?.appliedFiltersIds,
  (appliedFiltersIds = {}) => {
    const filtersLength = {};
    let filterCount = 0;

    Object.keys(appliedFiltersIds).forEach((key) => {
      if (appliedFiltersIds[key]) {
        const keysLength = appliedFiltersIds[key].length;
        filtersLength[`${key}Filters`] = keysLength;
        filterCount += keysLength;
      }
    });

    return {
      filtersLength,
      filterCount,
    };
  }
);

export const getAppliedSortId = createSelector(
  getSearchListingState,
  (products) => products && products.appliedSortId
);

export const getLoadedProductsCount = createSelector(getSearchListingState, (products) => {
  const allProducts = products && products.loadedProductsPages;
  const totalProductCount =
    (allProducts && allProducts.reduce((sum, item) => item.length + sum, 0)) || 0;
  return totalProductCount || 0;
});

export const getLongDescription = createSelector(
  getSearchListingState,
  (ProductListing) => ProductListing && ProductListing.currentListingDescription
);

export const getUnbxdId = createSelector(
  getSearchListingState,
  (products) => products && products.unbxdId
);

export const getLoadedProductsPages = createSelector(
  getSearchListingState,
  (products) => products && products.loadedProductsPages
);

export const getInitialFilterValues = createSelector(
  getAppliedFilters,
  getAppliedSortId,
  (appliedFilters, appliedSortId) => ({
    ...appliedFilters,
    sort: appliedSortId || '',
  })
);

export const getProductsFilters = createSelector(
  (state) => getSearchListingState(state)?.filtersMaps,
  (filtersMaps) => {
    if (filtersMaps) {
      return removeEmptyFilters(
        ['v_clothingsize_uFilter', 'v_accessoriessize_uFilter', 'v_shoessize_uFilter'],
        filtersMaps
      );
    }
    return {};
  }
);

export const getIsBopisFilterOn = createSelector(
  getSearchListingState,
  (products) => products && products.isBopisFilterOn
);

export const getIsDataLoading = (state) => {
  return state[SLP_PAGE_REDUCER_KEY].isDataLoading;
};

export const getIsLoadingMore = (state) => {
  return state[SLP_PAGE_REDUCER_KEY].isLoadingMore;
};
export const getSelectedPage = (state) => {
  return state[SLP_PAGE_REDUCER_KEY].selectedPage;
};

export const getModalState = (state) => {
  return state[SLP_PAGE_REDUCER_KEY].isKeepModalOpen;
};

export const checkIfSearchResultsAvailable = (state) => {
  return state[SLP_PAGE_REDUCER_KEY].isSearchResultsAvailable;
};

export const getFirstTimeCaptureBanner = () => {
  return noResponse;
};

export const getFirstTimeCaptureShopMenu = () => {
  return noResponse;
};

export const getAlternateBrandName = (state) => {
  return state[SLP_PAGE_REDUCER_KEY] && state[SLP_PAGE_REDUCER_KEY].alternateBrandName;
};
export const getSpotlightReviewsUrl = () => {
  return getAPIConfig().BAZAARVOICE_SPOTLIGHT;
};

export const getCategoryId = (state) => {
  const currentNavigationIds = state.ProductListing && state.ProductListing.currentNavigationIds;
  return currentNavigationIds && currentNavigationIds[currentNavigationIds.length - 1];
};

const getPageSize = () => {
  const isLowerEndDevice = isTier2Device() || isTier3Device();
  return isLowerEndDevice ? PRODUCTS_PER_LOAD_LOWER_END_DEVICES : PRODUCTS_PER_LOAD;
};

export const getRedirectObj = (state) => {
  return state[SLP_PAGE_REDUCER_KEY].redirect;
};

export const getLastLoadedPageNumber = (state) => {
  // note that we do not assume all pages have the same size, to protect against BE returning less products then requested.
  return Math.ceil(getLoadedProductsCount(state) / getPageSize());
};

export const getMaxPageNumber = (state) => {
  // We no longer need to divide by page size because UNBXD start parameter matches the direct number of results.
  return Math.ceil(state[SLP_PAGE_REDUCER_KEY].totalProductsCount / getPageSize());
};

/**
 * @function updateAppliedFiltersInState
 * matches filterMaps with appliedFilterIds
 * and updates isSelected state in filters
 *
 * @param {*} state
 * @returns
 */
export const updateAppliedFiltersInState = createSelector(
  getProductsFilters,
  getAppliedFilters,
  (filters, appliedFilters) => {
    const filterEntries = (filters && Object.entries(filters)) || [];
    if (appliedFilters && Object.keys(appliedFilters).length) {
      filterEntries.map((filter) => {
        const key = filter[0];
        const values = filter[1];
        const appliedFilterValue = appliedFilters[key] || [];
        if (!(values instanceof Array)) return values;

        // for all arrays in filters - update isSelected as true if it is present in appliedFilterIds
        values.map((value) => {
          const isValueApplied = appliedFilterValue.filter((id) => id === value.id).length > 0;
          const updatedValue = value;
          updatedValue.isSelected = isValueApplied;
          return updatedValue;
        });
        return values;
      });
    }

    return filters;
  }
);

export const getScrollToTopValue = createSelector(getSearchListingState, (slpState) => {
  return slpState.isScrollToTop;
});

const getModulesState = (state) => {
  return state.Modules;
};

const getTopPromosState = (state) => {
  const { searchListingPage: { top: topPromos } = {} } = state.Layouts;
  return topPromos;
};

const getLoyaltyPromosState = (state) => {
  const { searchListingPage: { loyaltyBanner: loyaltyPromo } = {} } = state.Layouts;
  return loyaltyPromo;
};

const flattenPromoSet = (promo) => {
  const promoSet = {};
  if (promo.set && Array.isArray(promo.set)) {
    promo.set.forEach((i) => {
      promoSet[i.key] = i.val;
    });
  }
  return promoSet;
};

const getPLPIngridPromoCondition = (promoItem, isGuest, isPlcc) => {
  const { guest, mpr, plcc } = flattenPromoSet(promoItem);

  return (guest && isGuest) || (plcc && isPlcc) || (!isGuest && !isPlcc && mpr);
};

const getPromoModuleInfo = ({ promoItem, modules, type }) => {
  let promoModuleInfo = (promoItem.contentId && modules[promoItem.contentId]) || {};
  if (type === 'grid' || type === 'horizontal') {
    promoModuleInfo = { ...promoModuleInfo, slot: promoItem && promoItem.slotNo };
  }
  return promoModuleInfo;
};

const getUserBasedPLPPromo = ({ promoItem, modules, isGuest, isPlcc, type, isRemembered }) => {
  let userBasedPLPPromo;
  const promoModuleInfo = getPromoModuleInfo({ promoItem, modules, type });
  switch (toLower(promoItem.name)) {
    case 'mpr':
      userBasedPLPPromo = (!isGuest || isRemembered) && !isPlcc && { ...promoModuleInfo };
      break;
    case 'plcc':
      userBasedPLPPromo = isPlcc && { ...promoModuleInfo };
      break;
    case 'guest':
      userBasedPLPPromo = isGuest && !isRemembered && { ...promoModuleInfo };
      break;
    default:
      userBasedPLPPromo = {};
  }
  return userBasedPLPPromo;
};

const emptyArray = [];
export const getSLPTopPromos = createSelector(
  getTopPromosState,
  getLoyaltyPromosState,
  getModulesState,
  getIsGuest,
  isPlccUser,
  isRememberedUser,
  (topPromos, loyaltyPromo, modules, isGuest, isPlcc, isRemembered) => {
    const loyaltyPromos =
      (loyaltyPromo &&
        loyaltyPromo.map((loyalPromo) => {
          const loyalPromoModule = loyalPromo.contentId && modules[loyalPromo.contentId];
          if (loyalPromoModule) {
            loyalPromoModule.userType = loyalPromo.name;
          }
          return loyalPromoModule || {};
        })) ||
      [];

    const promos =
      (topPromos &&
        topPromos.map((promoItem) => {
          if (promoItem.slotNo) {
            return getUserBasedPLPPromo({ promoItem, modules, isGuest, isPlcc, isRemembered });
          }
          if (getPLPIngridPromoCondition(promoItem, isGuest, isPlcc)) {
            return {};
          }
          return (promoItem.contentId && modules[promoItem.contentId]) || {};
        })) ||
      [];

    const resultPromos = loyaltyPromos.concat(promos).filter((item) => !isEmpty(item));

    return resultPromos.length > 0 ? resultPromos : emptyArray;
  }
);

export const getPLPGridPromos = (state) => {
  const { bannerInfo: { val: { grid: gridPromo } = {} } = {} } = state.SearchListingPage;
  const isGuest = getIsGuest(state);
  const isPlcc = isPlccUser(state);
  const isRemembered = isRememberedUser(state);
  const modules = state?.Modules;
  return (
    (gridPromo &&
      gridPromo.map((promoItem) => {
        const moduleInfo =
          (promoItem.val && promoItem.val.cid && state?.Modules[promoItem.val.cid]) || {};
        if (promoItem.slotNo) {
          return getUserBasedPLPPromo({
            promoItem,
            modules,
            isGuest,
            isPlcc,
            type: 'grid',
            isRemembered,
          });
        }
        if (getPLPIngridPromoCondition(promoItem, state?.Modules, isPlcc)) {
          return {};
        }
        return { ...moduleInfo, slot: promoItem?.val?.length && promoItem && promoItem.sub };
      })) ||
    []
  ).filter((item) => !isEmpty(item));
};

export const getPlpHorizontalPromo = (state) => {
  const { bannerInfo: { val: { horizontal: horizontalPromo } = {} } = {} } =
    state.SearchListingPage;
  const isGuest = getIsGuest(state);
  const isPlcc = isPlccUser(state);
  const isRemembered = isRememberedUser(state);
  const modules = state?.Modules;

  return (
    (horizontalPromo &&
      horizontalPromo.map((promoItem) => {
        const horizontalModuleInfo =
          (promoItem.val && promoItem.val.cid && state?.Modules[promoItem.val.cid]) || {};
        if (promoItem.slotNo) {
          return getUserBasedPLPPromo({
            promoItem,
            modules,
            isGuest,
            isPlcc,
            type: 'horizontal',
            isRemembered,
          });
        }
        if (getPLPIngridPromoCondition(promoItem, isGuest, isPlcc)) {
          return {};
        }
        return {
          ...horizontalModuleInfo,
          slot: promoItem?.val?.length && promoItem && promoItem.sub,
        };
      })) ||
    []
  ).filter((item) => !isEmpty(item));
};

export const getPlpCrossPromo = (state) => {
  const { bannerInfo: { val: { cross: crossPromo } = {} } = {} } = state.SearchListingPage;
  const isGuest = getIsGuest(state);
  const isPlcc = isPlccUser(state);
  const isRemembered = isRememberedUser(state);
  const modules = state?.Modules;

  return (
    (crossPromo &&
      crossPromo.map((promoItem) => {
        const crosslModuleInfo =
          (promoItem.val && promoItem.val.cid && state?.Modules[promoItem.val.cid]) || {};
        if (promoItem.slotNo) {
          return getUserBasedPLPPromo({
            promoItem,
            modules,
            isGuest,
            isPlcc,
            type: 'cross',
            isRemembered,
          });
        }
        if (getPLPIngridPromoCondition(promoItem, isGuest, isPlcc)) {
          return {};
        }
        return {
          ...crosslModuleInfo,
          slot: promoItem?.val?.length && promoItem && promoItem.sub,
        };
      })) ||
    []
  ).filter((item) => !isEmpty(item));
};

export const getShouldSlpScroll = (state) => {
  return state && state[SLP_PAGE_REDUCER_KEY] && state[SLP_PAGE_REDUCER_KEY].shouldSlpScroll;
};

export const getIsPLPFilterNewDesign = (state) => {
  return (
    state &&
    state[SESSIONCONFIG_REDUCER_KEY] &&
    parseBoolean(state[SESSIONCONFIG_REDUCER_KEY].siteDetails?.ENABLE_PLP_FILTER_NEW_DESIGN)
  );
};

export const getIsPlpAvailabilityFilter = (state) => {
  return (
    state &&
    state[SESSIONCONFIG_REDUCER_KEY] &&
    parseBoolean(state[SESSIONCONFIG_REDUCER_KEY].siteDetails?.ENABLE_PLP_AVAILABILITY_FILTER)
  );
};

export const getAlternateBrandApiCalled = (state) => {
  return state?.SearchListingPage?.acrossBrandApiCalled;
};

export const getCrossBrandProducts = (state) => {
  return state?.SearchListingPage?.crossBrandProducts;
};

export const getAlternateBrandsShowns = (state) => {
  return state?.SearchListingPage?.alternateBrandsShown;
};
export const getColorProductID = (state) => {
  return state?.SearchListingPage?.colorProductId;
};
